import { formatDistanceToNow } from "date-fns";
import { match } from "ts-pattern";

import type { IntegrationAppEnabledDataCollection } from "@ctrl/controller-rpc";

/**
 * The Routine table (type) id backing an enabled data collection, or `null`
 * when the collection has no table yet (still pending creation, or an errored
 * collection that never reached the ready state).
 */
export const dataCollectionTableId = (
  dc: IntegrationAppEnabledDataCollection
): string | null =>
  match(dc)
    .with({ status: "ready" }, { status: "force_refresh" }, (d) => d.table)
    .with({ status: "creation_pending" }, () => null)
    .with({ status: "error" }, (d) =>
      match(d.last_known_state)
        .with({ status: "ready" }, (s) => s.table)
        .with({ status: "creation_pending" }, () => null)
        .exhaustive()
    )
    .exhaustive();

/**
 * When an enabled data collection last successfully synchronized with the
 * third party, or `null` when unknown — either it never reached a ready state,
 * or it was last synced before the `last_synced` field existed.
 */
export const dataCollectionLastSynced = (
  dc: IntegrationAppEnabledDataCollection
): Date | null =>
  match(dc)
    .with(
      { status: "ready" },
      { status: "force_refresh" },
      (d) => d.last_synced ?? null
    )
    .with({ status: "creation_pending" }, () => null)
    .with({ status: "error" }, (d) =>
      match(d.last_known_state)
        .with({ status: "ready" }, (s) => s.last_synced ?? null)
        .with({ status: "creation_pending" }, () => null)
        .exhaustive()
    )
    .exhaustive();

/** The most recent `last_synced` across several collections, or `null`. */
export const latestDataCollectionLastSynced = (
  dcs: IntegrationAppEnabledDataCollection[]
): Date | null => {
  let latest: Date | null = null;
  for (const dc of dcs) {
    const synced = dataCollectionLastSynced(dc);
    if (synced && (!latest || synced > latest)) latest = synced;
  }
  return latest;
};

/**
 * Human-readable "last synced" fragment for status tooltips (e.g. "Last synced
 * 2 hours ago"), or `null` when the last sync time is unknown.
 */
export const lastSyncedText = (lastSynced: Date | null): string | null =>
  lastSynced
    ? `Last synced ${formatDistanceToNow(lastSynced, { addSuffix: true })}`
    : null;
